import { FC, useRef } from "react";
import { Box, Skeleton, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import GraphMap from "../../../../../components/charts/GraphMap";
import HeatLayer from "../../../../../components/charts/GraphMap/layers/HeatLayer";
import ScaleLegend from "../../../../../components/charts/GraphMap/legends/ScaleLegend";
import DropdownSelect from "../../../../../components/common/DropdownSelect";
import PaperCard from "../../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../../components/widgets/PaperCard/PaperCardHeader";
import { useFiltersRegistry } from "../../../../../context/FiltersProvider";
import {
	CountriesEmissionsResultsDTO,
	CountriesResultsPayload,
} from "../../../../../datasources/api-client/data-contracts";
import { ApiRoutes } from "../../../../../datasources/networking/apiRoutes";
import { useReadResource } from "../../../../../hooks/useResource";
import { TransportPerspective } from "../../../../../state/preferences";

interface Props {
	color: string;
	title: string;
	selectedSource: string;
	onSourceChange: (value: string) => void;
	perspective?: TransportPerspective;
}

const EmissionsMap: FC<Props> = ({ color, title, selectedSource, onSourceChange, perspective }) => {
	const containerRef = useRef<HTMLDivElement | null>(null);

	const { t } = useTranslation("result.transport");

	const { appliedFilters } = useFiltersRegistry();

	// Create a menu with object for this select beacuse we need different type for the menu value
	const menuOptions = [
		{ key: "trips-origin", label: t("starting country") },
		{ key: "trips-destination", label: t("destination country") },
	];

	const { data, isLoading, reload } = useReadResource<CountriesResultsPayload, CountriesEmissionsResultsDTO>(
		{
			route: ApiRoutes.ResultsCountryEmission,
			query: perspective ? { perspective } : undefined,
		},
		{ readMethod: "post" },
		{
			...appliedFilters.payload,
			source: selectedSource,
		}
	);

	const map = (
		<GraphMap tileProvider="draw" isLoading={isLoading} zoom={3} maxZoom={20} minZoom={2}>
			<HeatLayer color={color} data={data?.data} scale={data?.scale} />
		</GraphMap>
	);

	if (isLoading) {
		return <Skeleton variant="rounded" height="100%" width="100%" />;
	}

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader
					containerRef={containerRef}
					title={title}
					element={
						<DropdownSelect
							dropdownItems={menuOptions}
							value={selectedSource}
							onChange={onSourceChange}
							variant="outlined"
						/>
					}
					isLoading={isLoading}
					onReload={reload}
				/>
			}
			content={
				<Stack direction="column" height="100%" width="100%" textAlign="center" position={"relative"}>
					{map}
					<Box pt={1}>
						<ScaleLegend title="tC02" scale={data?.scale} baseColor={color} />
					</Box>
				</Stack>
			}
			sxContent={{ paddingRight: 0, paddingLeft: 0 }}
		/>
	);
};

export default EmissionsMap;
